/**
 * Content Moderation Page
 * Review and manage all flagged content across the platform
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  MenuItem,
  SelectChangeEvent,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState, CopyableText } from '../../components/common';
import {
  Card,
  CardContent,
  TableCell,
  TableHeaderCell,
  TableRow,
  SearchBox,
  Select,
  DatePicker,
  DataTablePagination,
  TableContainer
} from '../../components/ui';
import { formatDateTime } from '../../utils/dateUtils';
import { ContentModerationService } from '../../services/contentModeration/ContentModerationService';
import type {
  FlaggedContent,
  ContentType,
  ModerationStatus,
  PaginatedFlaggedContent,
} from '../../types/contentModeration';

type ContentTypeFilter = ContentType | 'all';
type StatusFilter = ModerationStatus | 'all';

const contentTypeLabels: Record<ContentType, string> = {
  'ai-marked': 'AI-marked',
  'user-reported': 'User-reported',
};

const statusLabels: Record<ModerationStatus, string> = {
  pending: 'Pending',
  reviewed: 'Reviewed',
  action_taken: 'Action Taken',
  dismissed: 'Dismissed',
};

const getStatusColor = (status: ModerationStatus, theme: any): string => {
  switch (status) {
    case 'pending':
      return theme.palette.warning.main;
    case 'reviewed':
      return theme.palette.info.main;
    case 'action_taken':
      return theme.palette.success.main;
    case 'dismissed':
      return theme.palette.grey[500];
    default:
      return theme.palette.text.secondary;
  }
};

const ContentModerationPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [content, setContent] = useState<FlaggedContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [contentTypeFilter, setContentTypeFilter] = useState<ContentTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // const [totalResults, setTotalResults] = useState(0); // Removed as it is not used
  const [totalPages, setTotalPages] = useState(0);

  const fetchContent = useCallback(async () => {
    try {
      setIsLoading(true);
      const result: PaginatedFlaggedContent =
        await ContentModerationService.getFlaggedContent(page, pageSize, {
          contentType: contentTypeFilter,
          status: statusFilter,
          search: debouncedSearchValue || undefined,
          dateRange:
            dateRangeStart && dateRangeEnd
              ? {
                start: new Date(dateRangeStart),
                end: new Date(dateRangeEnd + 'T23:59:59'),
              }
              : undefined,
        });
      setContent(result.data);
      // setTotalResults(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Failed to fetch flagged content:', error);
      setContent([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, contentTypeFilter, statusFilter, debouncedSearchValue, dateRangeStart, dateRangeEnd]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handleContentTypeChange = (event: SelectChangeEvent<unknown>, _child: React.ReactNode) => {
    setContentTypeFilter(event.target.value as ContentTypeFilter);
    setPage(1);
  };

  const handleStatusChange = (event: SelectChangeEvent<unknown>, _child: React.ReactNode) => {
    setStatusFilter(event.target.value as StatusFilter);
    setPage(1);
  };

  const handleDateRangeStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRangeStart(e.target.value);
    setPage(1);
  };

  const handleDateRangeEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRangeEnd(e.target.value);
    setPage(1);
  };

  const handleViewDetails = (content: FlaggedContent) => {
    navigate(`/content-moderation/${content.id}`);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          Content Moderation
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: theme.palette.text.secondary }}
        >
          Review and manage all flagged content across the platform.
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
          flexWrap: 'wrap',
          gap: theme.spacing(2),
        }}
      >
        {/* Search Box */}
        <SearchBox
          placeholder="Search by Content ID, Mission Code, or keyword..."
          value={searchValue}
          onChange={handleSearch}
          containerSx={{ flex: 1, minWidth: 300 }}
        />

        {/* Content Type Filter */}
        <Select
          value={contentTypeFilter}
          onChange={handleContentTypeChange}
          displayEmpty
          size="small"
          IconComponent={ArrowDownIcon}
          formControlProps={{ sx: { minWidth: 150 } }}
        >
          <MenuItem value="all">Content Type</MenuItem>
          <MenuItem value="ai-marked">AI-marked</MenuItem>
          <MenuItem value="user-reported">User-reported</MenuItem>
        </Select>

        {/* Status Filter */}
        <Select
          value={statusFilter}
          onChange={handleStatusChange}
          displayEmpty
          size="small"
          IconComponent={ArrowDownIcon}
          formControlProps={{ sx: { minWidth: 150 } }}
        >
          <MenuItem value="all">Status</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="reviewed">Reviewed</MenuItem>
          <MenuItem value="action_taken">Action Taken</MenuItem>
          <MenuItem value="dismissed">Dismissed</MenuItem>
        </Select>

        {/* Date Range Filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <DatePicker
            value={dateRangeStart}
            onChange={handleDateRangeStartChange}
            placeholder="Start Date"
            sx={{ width: 150 }}
          />
          <Typography sx={{ color: theme.palette.text.secondary }}>~</Typography>
          <DatePicker
            value={dateRangeEnd}
            onChange={handleDateRangeEndChange}
            placeholder="End Date"
            sx={{ width: 150 }}
          />
        </Box>
      </Box>

      {/* Content Table */}
      <Card>
        <CardContent noPadding>
          {isLoading ? (
            <Box sx={{ padding: theme.spacing(6), display: 'flex', justifyContent: 'center' }}>
              <LoadingSpinner />
            </Box>
          ) : content.length === 0 ? (
            <Box sx={{ padding: theme.spacing(4) }}>
              <EmptyState
                title="No Flagged Content Found"
                message="There are no flagged content items matching your criteria."
              />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Content ID</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell>Reason for Flagging</TableHeaderCell>
                      <TableHeaderCell>Mission Code</TableHeaderCell>
                      <TableHeaderCell>Date Flagged</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {content.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {item.contentId}
                        </TableCell>
                        <TableCell>
                          {contentTypeLabels[item.type]}
                        </TableCell>
                        <TableCell>
                          {item.reasonForFlagging}
                        </TableCell>
                        <TableCell>
                          <CopyableText
                            text={item.missionId}
                            onTextClick={() => navigate(`/missions/${item.missionId}`)}
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.secondary }}>
                          {formatDateTime(item.dateFlagged)}
                        </TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              padding: theme.spacing(0.5, 1.5),
                              borderRadius: theme.shapes.borderRadius.lg,
                              backgroundColor: getStatusColor(item.status, theme),
                              color: theme.palette.common.white,
                              fontWeight: theme.typography.button.fontWeight,
                              fontSize: theme.typography.caption.fontSize,
                            }}
                          >
                            {statusLabels[item.status]}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleViewDetails(item)}
                            sx={{
                              color: theme.palette.primary.main,
                              cursor: 'pointer',
                              textDecoration: 'none',
                              fontSize: theme.typography.body2.fontSize,
                              fontWeight: 400,
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            View
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <DataTablePagination
                totalPages={totalPages}
                page={page}
                onPageChange={handlePageChange}
                layout="space-between"
                showStats={true}
                startIndex={(page - 1) * pageSize + 1}
                endIndex={Math.min(page * pageSize, content.length * totalPages)} // Approximate total
                totalResults={content.length * totalPages} // Approximate total
                showPageSize={true}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default ContentModerationPage;

